import { Session } from 'next-auth';

import { getAppConfig } from '@services/config';

import { TagCommanderType, TrackingType } from '@app-types/tracking';
import { UserType } from '@app-types/user';

import { isServer } from '@helpers/config';
import { localApiPaths } from '@helpers/path';
import { getServerSession } from '@helpers/session';

class TrackingManager {
    private appConfig = getAppConfig();
    private readonly tag: Promise<TagCommanderType> | undefined;
    private isReady = false;
    private queue: { isRenewal: boolean; data: TrackingType }[] = [];

    private setupAttempts = 0;
    private readonly maxSetupAttempts = 3;

    constructor() {
        if (typeof window !== 'undefined') {
            this.tag = import('@bytel/bytel-tag-commander').then((module) => module.default as TagCommanderType);
        }
    }

    public setup(): TrackingManager {
        this.tag
            ?.then(async (tag) => {
                if (!tag) {
                    throw new Error('tracking must be setup from the browser');
                }

                await this.attemptSetup(tag);
            })
            .catch((error) => {
                console.log('Error setting up tracking:', error);
            });

        return this;
    }

    public async sendTracking(isRenewal: boolean, data: TrackingType) {
        const tag = await this.tag;

        if (!tag) {
            throw new Error('tracking must be sent from the browser');
        }

        if (!this.isReady) {
            this.queue.push({ isRenewal, data });
            return;
        }

        try {
            await tag.send(
                JSON.parse(
                    JSON.stringify({
                        site: data.site ?? this.getSite(),
                        univers: data.universe ?? 'Boutique_mobile',
                        parcours: data.journey ?? (isRenewal ? 'Renouvellement' : 'Acquisition'),
                        url: data.url ?? window.location.href,
                        page: data.page,
                        pageCategorie: data.pageCategory,
                        actionClic: data.action,
                        categorieClic: data.category,
                        nomClic: data.name,
                        ligneMarche: data.marketLine,
                        activiteB2BouB2C: data.isB2B ? 'B2B' : 'B2C',
                        etapeEcommerce: data.eCommerceStep,
                        pageVariante: data.variant,
                        variablesSpecifiques: data.variables,
                        produits: data.products,
                        panier: data.panier,
                    }),
                ),
            );
        } catch {}
    }

    private async attemptSetup(tag: TagCommanderType) {
        setTimeout(async () => {
            try {
                let user: UserType | undefined;
                let session: Session | null;
                try {
                    if (isServer()) {
                        session = await getServerSession();
                        user = session?.user as UserType | undefined;
                    } else {
                        const response = await fetch(localApiPaths.authentification.session);
                        if (response.ok) {
                            session = await response.json();
                            user = session?.user as UserType | undefined;
                        }
                    }
                } catch (error) {
                    console.log('Could not get user session for tracking setup', error);
                }

                await tag.setup({
                    host: this.appConfig.tracking.host,
                    debug: this.appConfig.tracking.debug,
                    env: this.appConfig.tracking.env,
                    site: this.getSite(),
                    oauth: user
                        ? {
                              id_personne: user.sub,
                              user_type: user.user_type,
                              accessToken: user.accessToken,
                          }
                        : {},
                    shouldEmitEvents: true,
                    hasHistory: true,
                });

                this.isReady = true;

                await Promise.all(this.queue.map(({ isRenewal, data }) => this.sendTracking(isRenewal, data)));
                this.queue = [];
            } catch (error) {
                this.setupAttempts++;
                if (this.setupAttempts < this.maxSetupAttempts) {
                    console.log(
                        `Retrying tracking setup (${this.setupAttempts}/${this.maxSetupAttempts}). Error: ${error}`,
                    );
                    setTimeout(() => this.attemptSetup(tag), 500 * this.setupAttempts);
                } else {
                    console.error(`Max tracking setup attempts reached. Clearing queue. Error: ${error}`);
                    this.queue = [];
                }
            }
        }, 0);
    }

    private getSite() {
        if (isServer()) {
            throw new Error('Could not get tracking site from server side');
        }

        const bankCartData = localStorage.getItem('yama_cart');
        const isActiveBankCart =
            bankCartData !== null && Number(JSON.parse(bankCartData)?.expiresTimestamp) > Date.now();
        return isActiveBankCart ? 'Partenaire_cfcm_boutique' : 'Boutique';
    }
}

export const trackingManager = new TrackingManager().setup();
